import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { isRequestIcon } from 'app/lib/is-request-icon';
import { RequestImage } from 'app/components/request-image';
import { navigateToListing, navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { ConciergeSignal } from './channel';
import { isMobileNative, useUISize } from 'app/utils/web-helpers';
import { useNavigation } from 'app/hooks/navigation';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Pressable } from 'react-native';
import type { Listing } from 'app/types/listing';
import { useProfile } from 'app/network/profile';
import { useOneListing } from 'app/network/listing';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronLeft } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface ChannelListHeaderProps {
  userId?: string;
  listingId?: Listing['id'];
  /**
   * Count any conversations for this channel
   */
  serverCount?: number;
  isNavigationOptionHeader?: boolean;
}

const ChannelHeaderFrame = ({
  children,
  isLargeUI,
  marginTop,
  marginBottom,
  onBack,
}: {
  children: React.ReactNode;
  isLargeUI: boolean;
  marginTop: number;
  marginBottom: number;
  onBack: () => void;
}) => (
  <Row
    style={{
      alignContent: 'center',
      marginTop,
      paddingLeft: theme.spacing['spacing.general.x2'],
      marginBottom,
    }}
    gap="spacing.general.x2"
    alignItems="center"
  >
    {!isLargeUI ? (
      <Pressable onPress={onBack}>
        <IconChevronLeft color={theme.colors['white-label.header.on-header']} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>
    ) : null}
    {children}
  </Row>
);

const ConversationCount = ({ count, t }: { count: number | undefined; t: TFunction }) => {
  if (typeof count !== 'number') return null;
  return (
    <Text color="white-label.header.on-header" variant="bodyBase">
      {t('groupConversationCount', { count })}
    </Text>
  );
};

const UserChannelHeader = ({
  avatarSource,
  firstName,
  onPress,
  serverCount,
  t,
}: {
  avatarSource: { uri: string } | undefined;
  firstName: string | undefined;
  onPress: () => void;
  serverCount: number | undefined;
  t: TFunction;
}) => (
  <Pressable onPress={onPress}>
    <Row gap="spacing.general.x3" alignItems="center">
      <PCImage aspectRatio={1} height={40} source={avatarSource} style={{ borderRadius: 20 }} />
      <Column justifyContent="center">
        <Text
          fontWeight="700"
          numberOfLines={1}
          variant="bodyBase"
          style={{ color: theme.colors['white-label.header.on-header'] }}
        >
          {firstName}
        </Text>
        <ConversationCount count={serverCount} {...{ t }} />
      </Column>
    </Row>
  </Pressable>
);

const ListingChannelHeader = ({
  isConciergeChannel,
  listing,
  onPress,
  serverCount,
  t,
}: {
  isConciergeChannel: boolean;
  listing: Listing;
  onPress: () => void;
  serverCount: number | undefined;
  t: TFunction;
}) => (
  <Pressable onPress={onPress}>
    <Row gap="spacing.general.x3" alignItems="center">
      {isRequestIcon(listing.picture) ? (
        <RequestImage width={40} height={40} isFound={Boolean(listing.lender)} />
      ) : (
        <PCImage
          aspectRatio={1}
          height={40}
          source={{ uri: listing.picture ?? undefined }}
          style={{ borderRadius: theme.borderRadii['radius.sm'] }}
        />
      )}
      <Column justifyContent="center">
        <Row gap="spacing.general.x1" alignItems="center">
          {isConciergeChannel ? <ConciergeSignal /> : null}
          <Text fontWeight="700" numberOfLines={1} style={{ color: theme.colors['white-label.header.on-header'] }}>
            {listing.item.name}
          </Text>
        </Row>
        <ConversationCount count={serverCount} {...{ t }} />
      </Column>
    </Row>
  </Pressable>
);

export const ChannelListHeader = function ChannelListHeader({
  userId,
  listingId,
  serverCount,
  isNavigationOptionHeader = true,
}: ChannelListHeaderProps) {
  const navigation = useNavigation<'one-channel'>();
  const { t } = useTranslation('all-channels');
  const insets = useSafeAreaInsets();
  const extraMargin = isMobileNative ? 0 : theme.spacing['spacing.general.x4'];
  const maring = isNavigationOptionHeader ? insets.top + extraMargin : 0;
  const { data: profile } = useProfile(userId);
  const { data: listing } = useOneListing(listingId, 'channel-list-header');
  const { isLargeUI } = useUISize();
  const avatarSource = profile?.picture ? { uri: profile.picture } : undefined;

  if (userId) {
    return (
      <ChannelHeaderFrame
        {...{ isLargeUI }}
        marginBottom={isNavigationOptionHeader ? theme.spacing['spacing.general.x2'] : 0}
        marginTop={maring}
        onBack={() => navigation.goBack()}
      >
        <UserChannelHeader
          {...{ avatarSource, serverCount, t }}
          firstName={profile?.firstName}
          onPress={() => navigateToUserProfile(navigation, userId)}
        />
      </ChannelHeaderFrame>
    );
  }

  if (!listingId || !listing) return null;
  const isConciergeChannel = (listing.needMatches?.length ?? 0) > 0;

  return (
    <ChannelHeaderFrame
      {...{ isLargeUI }}
      marginBottom={isNavigationOptionHeader ? theme.spacing['spacing.general.x2'] : 0}
      marginTop={maring}
      onBack={() => navigation.goBack()}
    >
      <ListingChannelHeader
        {...{ isConciergeChannel, listing, serverCount, t }}
        onPress={() => navigateToListing(navigation, listingId, false)}
      />
    </ChannelHeaderFrame>
  );
};
